import { FormEvent, useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { Alert, Badge, Button, Form, Modal, Spinner } from 'react-bootstrap';

export type ScopedServiceRecord = {
  id: number;
  code: string;
  display_name: string;
  is_active: boolean;
  locations: Array<{ id: number; code: string; display_name: string }>;
  roles?: Array<{ id: number; starts_at?: string | null; ends_at?: string | null; coverage_pattern?: { days?: string[] } | null }>;
  coverage_days?: string[] | null;
  coverage_hours?: string[] | null;
};

type LocationOption = { id: number; code: string; display_name: string; group_id?: number | null };
type ServiceIndexResponse = { data: { data: ScopedServiceRecord[] } };
type LocationIndexResponse = { data: { data: LocationOption[] } };
type ServiceDraft = { id: number | null; code: string; display_name: string; location_ids: number[]; is_active: boolean };
type FieldErrors = Partial<Record<'code' | 'display_name' | 'location_ids', string>>;
type ReviewContext = {
  services: ScopedServiceRecord[];
  loading: boolean;
  busyRecordId: number | null;
  deleteConfirmId: number | null;
  editService: (record: ScopedServiceRecord) => void;
  deleteService: (record: ScopedServiceRecord) => void;
  addService: () => void;
  locationOptions: LocationOption[];
};

const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') ?? '';
const emptyDraft: ServiceDraft = { id: null, code: '', display_name: '', location_ids: [], is_active: true };
const dayLabels: Record<string, string> = { sun: 'Sun', mon: 'Mon', tue: 'Tue', wed: 'Wed', thu: 'Thu', fri: 'Fri', sat: 'Sat' };

const readFailure = async (response: Response, fallback: string) => {
  const payload = await response.json().catch(() => null) as { message?: string; errors?: Record<string, string[]> } | null;
  return payload?.errors ? Object.values(payload.errors).flat().join(' ') : payload?.message ?? fallback;
};

export default function GroupServiceWorkspace({ tenantQuery, reviewWorkspace }: {
  tenantQuery: string;
  reviewWorkspace: (context: ReviewContext) => ReactNode;
}) {
  const [services, setServices] = useState<ScopedServiceRecord[]>([]);
  const [locations, setLocations] = useState<LocationOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [busyRecordId, setBusyRecordId] = useState<number | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [draft, setDraft] = useState<ServiceDraft>(emptyDraft);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const query = useMemo(() => {
    const params = new URLSearchParams(tenantQuery);
    params.set('per_page', '100');
    return params.toString();
  }, [tenantQuery]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [serviceResponse, locationResponse] = await Promise.all([
        fetch(`/api/v1/secure/schedule-settings/services?${query}`, { credentials: 'same-origin', headers: { Accept: 'application/json' } }),
        fetch(`/api/v1/secure/schedule-settings/locations?${query}`, { credentials: 'same-origin', headers: { Accept: 'application/json' } }),
      ]);
      if (!serviceResponse.ok) throw new Error(await readFailure(serviceResponse, `Unable to load services (${serviceResponse.status}).`));
      if (!locationResponse.ok) throw new Error(await readFailure(locationResponse, `Unable to load locations (${locationResponse.status}).`));
      const servicePayload = await serviceResponse.json() as ServiceIndexResponse;
      const locationPayload = await locationResponse.json() as LocationIndexResponse;
      setServices(servicePayload.data.data
        .map(service => ({
          ...service,
          coverage_days: [...new Set((service.roles ?? []).flatMap(role => role.coverage_pattern?.days ?? []).map(day => dayLabels[day.toLowerCase()] ?? day))],
          coverage_hours: [...new Set((service.roles ?? []).map(role => role.starts_at && role.ends_at ? `${role.starts_at.slice(0, 5)}–${role.ends_at.slice(0, 5)}` : null).filter((value): value is string => Boolean(value)))],
        }))
        .sort((left, right) => left.display_name.localeCompare(right.display_name)));
      setLocations(locationPayload.data.data
        .filter(location => location.group_id == null)
        .sort((left, right) => left.display_name.localeCompare(right.display_name)));
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Unable to load services.');
    } finally {
      setLoading(false);
    }
  }, [query]);

  useEffect(() => { void load(); }, [load]);

  const openCreate = () => {
    setDraft(emptyDraft);
    setFieldErrors({});
    setError(null);
    setShowModal(true);
  };

  const editService = (record: ScopedServiceRecord) => {
    setDraft({ id: record.id, code: record.code, display_name: record.display_name, location_ids: record.locations.map(location => location.id), is_active: record.is_active });
    setFieldErrors({});
    setError(null);
    setShowModal(true);
  };

  const save = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const errors: FieldErrors = {};
    if (!draft.code.trim()) errors.code = 'Service code is required.';
    if (!draft.display_name.trim()) errors.display_name = 'Service name is required.';
    if (!draft.location_ids.length) errors.location_ids = 'Select at least one Company Admin location.';
    if (Object.keys(errors).length) { setFieldErrors(errors); return; }
    setSaving(true); setError(null); setSuccess(null); setFieldErrors({});
    try {
      const response = await fetch(
        draft.id === null
          ? `/api/v1/secure/schedule-settings/services?${tenantQuery}`
          : `/api/v1/secure/schedule-settings/services/${draft.id}?${tenantQuery}`,
        {
          method: draft.id === null ? 'POST' : 'PATCH',
          credentials: 'same-origin',
          headers: { Accept: 'application/json', 'Content-Type': 'application/json', 'X-CSRF-TOKEN': csrfToken },
          body: JSON.stringify({ code: draft.code.trim(), display_name: draft.display_name.trim(), location_ids: draft.location_ids, is_active: draft.is_active }),
        }
      );
      if (!response.ok) throw new Error(await readFailure(response, `Unable to save service (${response.status}).`));
      setShowModal(false);
      setSuccess(draft.id === null ? 'Service created.' : 'Service updated.');
      await load();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Unable to save service.');
    } finally {
      setSaving(false);
    }
  };

  const deleteService = async (record: ScopedServiceRecord) => {
    if (deleteConfirmId !== record.id) { setDeleteConfirmId(record.id); return; }
    setBusyRecordId(record.id); setError(null); setSuccess(null);
    try {
      const response = await fetch(`/api/v1/secure/schedule-settings/services/${record.id}?${tenantQuery}`, {
        method: 'DELETE', credentials: 'same-origin', headers: { Accept: 'application/json', 'X-CSRF-TOKEN': csrfToken },
      });
      if (!response.ok) throw new Error(await readFailure(response, `Unable to delete service (${response.status}).`));
      setServices(current => current.filter(service => service.id !== record.id));
      setDeleteConfirmId(null);
      setSuccess('Service deleted.');
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Unable to delete service.');
    } finally {
      setBusyRecordId(null);
    }
  };

  return <div className="d-flex flex-column gap-3">
    {error ? <Alert variant="danger" className="mb-0">{error}</Alert> : null}
    {success ? <Alert variant="success" className="mb-0">{success}</Alert> : null}
    {reviewWorkspace({ services, loading, busyRecordId, deleteConfirmId, editService, deleteService: record => void deleteService(record), addService: openCreate, locationOptions: locations })}

    <Modal show={showModal} onHide={() => setShowModal(false)} size="lg" centered>
      <Form onSubmit={save} noValidate>
        <Modal.Header closeButton><Modal.Title>{draft.id === null ? 'Add Service' : 'Edit Service'}</Modal.Title></Modal.Header>
        <Modal.Body>
          <div className="row g-3">
            <div className="col-md-4"><Form.Group><Form.Label>Code</Form.Label><Form.Control value={draft.code} onChange={event => setDraft(current => ({ ...current, code: event.target.value }))} isInvalid={Boolean(fieldErrors.code)} autoFocus /><Form.Text>Codes are normalized to uppercase on save.</Form.Text><Form.Control.Feedback type="invalid">{fieldErrors.code}</Form.Control.Feedback></Form.Group></div>
            <div className="col-md-8"><Form.Group><Form.Label>Service name</Form.Label><Form.Control value={draft.display_name} onChange={event => setDraft(current => ({ ...current, display_name: event.target.value }))} isInvalid={Boolean(fieldErrors.display_name)} /><Form.Control.Feedback type="invalid">{fieldErrors.display_name}</Form.Control.Feedback></Form.Group></div>
            <div className="col-12"><Form.Group><Form.Label>Locations</Form.Label><Form.Select multiple value={draft.location_ids.map(String)} isInvalid={Boolean(fieldErrors.location_ids)} onChange={event => setDraft(current => ({ ...current, location_ids: Array.from(event.target.selectedOptions).map(option => Number(option.value)) }))}>{locations.map(location => <option key={location.id} value={location.id}>{location.display_name} ({location.code})</option>)}</Form.Select><Form.Text>These locations come from the approved Company Admin locations available to this Schedule Group. Hold Ctrl/Cmd to select multiple locations.</Form.Text><Form.Control.Feedback type="invalid">{fieldErrors.location_ids}</Form.Control.Feedback></Form.Group></div>
            <div className="col-12"><Form.Check type="switch" label="Active" checked={draft.is_active} onChange={event => setDraft(current => ({ ...current, is_active: event.target.checked }))} /></div>
          </div>
        </Modal.Body>
        <Modal.Footer><Button variant="light" type="button" onClick={() => setShowModal(false)}>Cancel</Button><Button type="submit" disabled={saving}>{saving ? <><Spinner animation="border" size="sm" className="me-2" />Saving</> : draft.id === null ? 'Add Service' : 'Save changes'}</Button></Modal.Footer>
      </Form>
    </Modal>
  </div>;
}
